import { Link } from "react-router-dom";
import styled from "styled-components";
import { StaggerReveal, MotionItem } from "../../ui/Motion";

export const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing["5xl"]};
  align-items: center;
  padding: ${({ theme }) => `${theme.spacing["3xl"]} 0`};

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing["3xl"]};
    padding: ${({ theme }) => `${theme.spacing["2xl"]} 0`};
  }
`;

export const ImageSide = styled(MotionItem)`
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 5;
  min-height: 520px;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    min-height: 0;
    aspect-ratio: 5 / 4;
  }
`;

const collageImage = `
  position: absolute;
  display: block;
  object-fit: cover;
  border-radius: 14px;
  box-shadow:
    0 2px 4px rgba(15, 23, 42, 0.08),
    0 12px 28px rgba(15, 23, 42, 0.18),
    0 32px 64px rgba(15, 23, 42, 0.22);
`;

export const CollageMain = styled.img`
  ${collageImage}
  top: 0;
  left: 0;
  width: 70%;
  height: 78%;
  z-index: 1;
`;

export const CollageOffsetTop = styled.img`
  ${collageImage}
  top: 8%;
  right: 0;
  width: 46%;
  height: 38%;
  z-index: 2;
`;

export const CollageOffsetBottom = styled.img`
  ${collageImage}
  bottom: 0;
  right: 6%;
  width: 56%;
  height: 44%;
  z-index: 3;
`;

export const TextContent = styled(StaggerReveal)``;

export const Title = styled.h2`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(2rem, 4vw, ${({ theme }) => theme.fontSizes["5xl"]});
  font-weight: ${({ theme }) => theme.fontWeights.light};
  line-height: ${({ theme }) => theme.lineHeights.tight};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: ${({ theme }) => theme.spacing.xl};

  em {
    font-style: italic;
    color: ${({ theme }) => theme.colors.primary};
  }
`;

export const Description = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  line-height: ${({ theme }) => theme.lineHeights.relaxed};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing["2xl"]};
`;

export const StatsRow = styled(StaggerReveal)`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing["2xl"]};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    gap: ${({ theme }) => theme.spacing.sm};
  }
`;

export const Stat = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  text-align: center;
  flex: 1;
  min-width: 0;
  padding: 20px 24px;
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid rgba(15, 23, 42, 0.06);
  box-shadow:
    0 1px 2px rgba(15, 23, 42, 0.04),
    0 2px 6px rgba(15, 23, 42, 0.04);

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 14px 12px;
  }
`;

export const StatIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: rgba(112, 141, 171, 0.12);
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.sm};

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const StatNumber = styled.p`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: ${({ theme }) => theme.fontSizes["4xl"]};
  font-weight: ${({ theme }) => theme.fontWeights.light};
  color: ${({ theme }) => theme.colors.textPrimary};
  line-height: 1;
  margin-bottom: ${({ theme }) => theme.spacing.xs};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: ${({ theme }) => theme.fontSizes["2xl"]};
  }
`;

export const StatLabel = styled.p`
  font-family: ${({ theme }) => theme.fonts.caps};
  font-size: 10px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  letter-spacing: ${({ theme }) => theme.letterSpacings.widest};
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

export const Divider = styled.hr`
  border: none;
  height: 1px;
  background: rgba(15, 23, 42, 0.08);
  margin: ${({ $margin }) => $margin || "2rem 0"};
`;

export const LearnMoreLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  font-family: ${({ theme }) => theme.fonts.caps};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  letter-spacing: ${({ theme }) => theme.letterSpacings.wider};
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.primary};
  transition: all ${({ theme }) => theme.transitions.fast};

  &::after {
    content: "→";
    transition: transform ${({ theme }) => theme.transitions.fast};
  }

  &:hover::after {
    transform: translateX(4px);
  }
`;
